import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { GraduationCap } from 'lucide-react';
import WalkthroughModal from './WalkthroughModal';

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

/**
 * Opens the walkthrough on its own until someone's setup is genuinely finished, and stays in
 * Quick Actions so they can find it again.
 *
 * It used to record "seen" on the FIRST CLOSE — the X, Escape, or a click outside. Someone who
 * glanced at it was marked done for good, on every device, and logging out could not bring it
 * back because "seen" is per account by design (owner, Jun 2026: "I had her log out and log
 * back in, but it didn't pop up again. This is a limitation."). Now closing it means "not now",
 * and only reaching the end — or ticking "don't show me this again" — stops it.
 *
 * Once per browser session, so it does not reopen every time they change tab.
 */
export default function WalkthroughButton({ hub = 'practitioner', className = '' }) {
  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState(false);
  const [resume, setResume] = useState(null);
  const autoOpened = useRef(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/user/walkthrough`, auth())
      .then((r) => {
        if (!alive) return;
        const state = r.data?.state?.[hub] || {};
        setResume(state.resume || null);
        const shownThisSession = sessionStorage.getItem(`walkthroughShown:${hub}`) === '1';
        if (state.should_auto_open && !shownThisSession) {
          sessionStorage.setItem(`walkthroughShown:${hub}`, '1');
          autoOpened.current = true;
          setOpen(true);
        }
      })
      .catch(() => {})
      .finally(() => { if (alive) setChecked(true); });
    return () => { alive = false; };
  }, [hub]);

  // Closed without finishing: "not now". It opens again next visit.
  const dismiss = useCallback(() => {
    setOpen(false);
    axios.post(`${API}/user/walkthrough/${hub}/dismissed`, {}, auth()).catch(() => {});
  }, [hub]);

  // Reached the end, or asked not to see it again.
  const finish = useCallback(() => {
    setOpen(false);
    axios.post(`${API}/user/walkthrough/${hub}/seen`, {}, auth()).catch(() => {});
  }, [hub]);

  const remember = useCallback((category, step) => {
    setResume({ category, step });
    axios.post(`${API}/user/walkthrough/${hub}/progress`, { category, step }, auth())
      .catch(() => {});
  }, [hub]);

  return (
    <>
      <Button
        variant="outline"
        className={`border-amber-500/40 text-amber-300 hover:bg-amber-500/10 ${className}`}
        onClick={() => setOpen(true)}
        disabled={!checked}
        data-testid="open-walkthrough-btn"
      >
        <GraduationCap className="mr-2 h-4 w-4" /> Setup Walkthroughs
      </Button>
      {open && (
        <WalkthroughModal
          hub={hub}
          resume={resume}
          onProgress={remember}
          onDismiss={dismiss}
          onFinish={finish}
        />
      )}
    </>
  );
}
